import * as React from 'react';

export const MOBILE_BREAKPOINT = 768;
// Tailwind's `lg` breakpoint, where admin detail pages switch to their
// side-by-side desktop layout.
const DESKTOP_LAYOUT_BREAKPOINT = 1024;

function useIsBelowWidth(breakpointPx: number) {
    const [isBelow, setIsBelow] = React.useState<boolean>(
        () => window.innerWidth < breakpointPx,
    );

    React.useEffect(() => {
        if (typeof window.matchMedia !== 'function') {
            return;
        }
        const mql = window.matchMedia(`(max-width: ${breakpointPx - 1}px)`);
        const onChange = () => {
            setIsBelow(window.innerWidth < breakpointPx);
        };
        mql.addEventListener('change', onChange);
        return () => mql.removeEventListener('change', onChange);
    }, [breakpointPx]);

    return isBelow;
}

export function useIsMobile() {
    return useIsBelowWidth(MOBILE_BREAKPOINT);
}

export function useIsDesktopLayout() {
    return !useIsBelowWidth(DESKTOP_LAYOUT_BREAKPOINT);
}

// A touch screen from phone width up to iPad Pro portrait width. These get
// the phone's overlays instead of desktop splits, which crush the page at
// this width. Matching on the pointer keeps narrow desktop windows on the
// desktop layout.
const TOUCH_TABLET_QUERY = `(pointer: coarse) and (min-width: ${MOBILE_BREAKPOINT}px) and (max-width: ${DESKTOP_LAYOUT_BREAKPOINT}px)`;

export function useIsTouchTablet() {
    const [matches, setMatches] = React.useState<boolean>(
        () =>
            typeof window.matchMedia === 'function' &&
            window.matchMedia(TOUCH_TABLET_QUERY).matches,
    );

    React.useEffect(() => {
        if (typeof window.matchMedia !== 'function') {
            return;
        }
        const mql = window.matchMedia(TOUCH_TABLET_QUERY);
        const onChange = () => {
            setMatches(mql.matches);
        };
        mql.addEventListener('change', onChange);
        return () => mql.removeEventListener('change', onChange);
    }, []);

    return matches;
}
